import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { useRecoilValue } from 'recoil'
import { FormProvider, useForm } from 'react-hook-form'
import vendorAtom from '@src/atom/vendor'

import Header from './components/Header'
import ProfileContainer from './components/ProfileContainer'

import BrandInfoSection from './components/BrandInfoForm'
import { yupResolver } from '@hookform/resolvers/yup'
import { vendorProfileSchema } from './schema/vendorProfileSchema'
import { VendorProfileFormType } from './type'
import useGetVendorProfileQuery from '@src/hooks/queries/vendor/useGetVendorProfileQuery'

import BusinessInfoForm from './components/BusinessInfoForm'
import ContactInfoForm from './components/ContactInfoForm'
import Footer from './components/Footer'
import useUploadFile from '@src/hooks/queries/file/useUploadFile'
import useUpdateVendorProfileMutate from '@src/hooks/queries/vendor/useUpdateVendorProfileMutate'
import { Modal, message } from 'antd'
import { useBlocker } from 'react-router-dom'

const BrandProfilePage = () => {
	const vendor = useRecoilValue(vendorAtom)
	const [profileImg, setProfileImg] = useState<Blob | null | undefined>()
	const [backgroundImg, setBackgroundImg] = useState<Blob | null | undefined>()

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return Object.keys(methods.formState.dirtyFields).length > 0 && currentLocation !== nextLocation
	})

	const { data: vendorProfileData } = useGetVendorProfileQuery(vendor.no, {
		enabled: !!(vendor.isLoggedIn && vendor.no),
		suspense: true,
	})
	const { mutateAsync: uploadMutateAsync } = useUploadFile()
	const { mutate: updateProfileMutate } = useUpdateVendorProfileMutate(vendor.no)

	const vendorProfile = useMemo(() => {
		if (!vendorProfileData) return undefined
		const { vendor_info: vendorInfo, vendor_contacts: vendorContacts } =
			vendorProfileData.data.vendor

		return {
			profileImg: vendorInfo?.profile_img,
			backgroundImg: vendorInfo?.background_img,
			name: vendorInfo?.name,
			vendorStoreLink: vendorInfo?.vendor_store_link,
			companyDescription: vendorInfo?.company_description,
			companyName: vendorInfo?.company_name,
			ceoName: vendorInfo?.ceo_name,
			address: vendorInfo?.address,
			email: vendorInfo?.email,
			phone: vendorInfo?.phone,
			businessRegistrationNumber: vendorInfo?.business_registration_number,
			salesReportNumber: vendorInfo?.sales_report_number,
			vendorContacts,
		} as VendorProfileFormType
	}, [vendorProfileData])

	const methods = useForm<VendorProfileFormType>({
		defaultValues: {
			profileImg: '',
			backgroundImg: '',
			name: '',
			vendorStoreLink: '',
			companyDescription: '',
			companyName: '',
			address: '',
			email: '',
			phone: '',
		},
		values: vendorProfile,
		resolver: yupResolver(vendorProfileSchema),
		mode: 'onTouched',
	})

	const handleSubmit = async (data: VendorProfileFormType) => {
		let uploadProfileImg
		let uploadBackgroundImg
		if (profileImg) {
			const formData = new FormData()
			formData.append('file', profileImg)
			formData.append('path', 'commerce/vendor/profile_img/')
			uploadProfileImg = uploadMutateAsync(formData)
		}

		if (backgroundImg) {
			const formData = new FormData()
			formData.append('file', backgroundImg)
			formData.append('path', 'commerce/vendor/background_img/')
			uploadBackgroundImg = uploadMutateAsync(formData)
		}

		const [uploadedProfileImg, uploadedBackgroundImg] = await Promise.all([
			uploadProfileImg,
			uploadBackgroundImg,
		])

		const {
			name,
			vendorStoreLink,
			companyDescription,
			companyName,
			ceoName,
			address,
			email,
			phone,
		} = methods.formState.dirtyFields

		const body = {
			name: name ? data.name : undefined,
			vendor_store_link: vendorStoreLink ? data.vendorStoreLink : undefined,
			company_description: companyDescription ? data.companyDescription : undefined,
			company_name: companyName ? data.companyName : undefined,
			ceo_name: ceoName ? data.ceoName : undefined,
			address: address ? data.address : undefined,
			email: email ? data.email : undefined,
			phone: phone ? data.phone : undefined,
			vendor_contacts: data.vendorContacts,
			profile_img: uploadedProfileImg?.data.upload_results[0].file_name,
			background_img:
				data.backgroundImg === null
					? null
					: uploadedBackgroundImg?.data.upload_results[0].file_name,
		}

		updateProfileMutate(body, {
			async onSuccess() {
				methods.reset(data)
				await message.info('저장 되었어요')
			},
		})
		return 'success'
	}

	return (
		<>
			<Wrapper>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(handleSubmit)}>
						<Header />
						<ProfileContainer>
							<BrandInfoSection
								username={vendor.username}
								profileImgState={profileImg}
								setProfileImg={setProfileImg}
								backgroundImgState={backgroundImg}
								setBackgroundImg={setBackgroundImg}
							/>
							<BusinessInfoForm />
							<ContactInfoForm />
						</ProfileContainer>
						<Footer />
					</form>
				</FormProvider>
			</Wrapper>
			<Modal
				open={blocker.state === 'blocked'}
				centered
				closable={false}
				title="페이지에서 나가시겠어요?"
				onOk={blocker.proceed}
				onCancel={blocker.reset}
				okText="나가기"
				cancelText="취소"
				okButtonProps={{
					danger: true,
				}}
			>
				페이지를 나가시면 입력한 내용이 모두 사라집니다.
			</Modal>
		</>
	)
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 5.3rem;
	margin-bottom: 9.7rem;
	padding-left: 7.1rem;
	width: 90rem;
`

export default BrandProfilePage
